import { fetchBaseQuery, BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import type { RootState } from '../app/store';
import { setKeycloakToken } from '../features/auth/authSlice';
import keycloak from './keycloak';
import { ENV } from './env';

const API_VERSION = ENV.API_VERSION;
// Allow API_URL to be either a relative path (`/api`) for same-origin nginx
// proxying, or a full URL (`https://pmt-api.codespiresolutions.cloud/api`) for
// cross-origin direct calls to the backend.
const API_URL = ENV.API_URL || '/api';

// Feature API slices hardcode paths like "/api/v1/notifications".
// When ENV.API_URL is a full URL (cross-origin deploy), prepend its origin so
// requests go directly to the backend instead of the SPA's own host.
function resolveBaseUrl(path: string): string {
  if (path.startsWith('/') && API_URL.startsWith('http')) {
    try {
      return `${new URL(API_URL).origin}${path}`;
    } catch {
      return path;
    }
  }
  return path;
}

function makeFetchBaseQuery(baseUrl: string) {
  return fetchBaseQuery({
    baseUrl: resolveBaseUrl(baseUrl),
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.accessToken;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  });
}

/**
 * Wraps fetchBaseQuery with a Keycloak token-refresh retry on 401.
 * When a request returns 401, we attempt updateToken(30) once.
 * If the refresh succeeds the request is retried with the new token.
 * Only if the refresh itself fails do we let the 401 propagate (triggering logout).
 */
export function createAuthBaseQuery(baseUrl: string): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> {
  const baseQuery = makeFetchBaseQuery(baseUrl);

  return async (args, api, extraOptions) => {
    let result = await baseQuery(args, api, extraOptions);

    if (result.error?.status === 401) {
      try {
        await keycloak.updateToken(30);
        if (keycloak.token) {
          api.dispatch(setKeycloakToken(keycloak.token));
          // Retry original request — prepareHeaders will pick up the new token
          result = await baseQuery(args, api, extraOptions);
        }
      } catch {
        // Refresh failed — let the 401 propagate so apiErrorMiddleware handles logout
      }
    }

    return result;
  };
}

/** Pre-configured base query with the default API version prefix */
export const authBaseQuery = createAuthBaseQuery(`${API_URL}/${API_VERSION}`);
